import { useRef, useState } from 'react';
import { PuzzleKind, PuzzleSummary } from '../../shared/types';
import { api } from '../api';
import { getPlayerToken, switchSession } from '../cookie';

interface Session {
  playerToken: string;
  commonName: string;
  puzzleCount: number;
}

interface Props {
  kind: PuzzleKind;
  // Loaded once in App and passed down (null while loading) so each list view
  // renders instantly without its own round trip.
  puzzles: PuzzleSummary[] | null;
  error: boolean;
  completions: Record<string, number>;
  skips: Set<number>;
  onSelect: (puzzleId: number) => void;
  onCreate?: () => void;
  onOpenCommunity?: () => void;
  onOpenOfficial?: () => void;
  onOpenTips?: () => void;
  onOpenDrill?: () => void;
  onOpenPlayers?: () => void;
}

export function PuzzleList({ kind, puzzles, error, completions, skips, onSelect, onCreate, onOpenCommunity, onOpenOfficial, onOpenTips, onOpenDrill, onOpenPlayers }: Props) {
  const [sessionModal, setSessionModal] = useState(false);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [sessionsError, setSessionsError] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function startLongPress(e: React.MouseEvent | React.TouchEvent) {
    e.preventDefault();
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null;
      setSessionModal(true);
      api.getSessions().then(setSessions).catch(() => setSessionsError(true));
    }, 600);
  }

  function cancelLongPress() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  function handleSessionSwitch(token: string, name: string) {
    if (token === getPlayerToken()) return;
    switchSession(token, name);
    window.location.reload();
  }

  // Anchor handler: let modifier-clicks fall through (open in new tab etc.),
  // otherwise navigate client-side. Mirrors the puzzle-card links.
  const navClick = (cb: () => void) => (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    cb();
  };

  if (error) return <p className="error">Failed to load puzzles.</p>;
  if (!puzzles) return <p className="loading">Loading puzzles…</p>;

  const currentToken = getPlayerToken();
  // The list arrives newest-first (id DESC), so the first unsolved entry is the
  // most recently added puzzle the player still owes.
  const nextPuzzle = puzzles.find(p => !(String(p.id) in completions));

  return (
    <div id="view-list">
      <h1>Gib<span
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        onMouseDown={startLongPress}
        onMouseUp={cancelLongPress}
        onMouseLeave={cancelLongPress}
        onTouchStart={startLongPress}
        onTouchEnd={cancelLongPress}
        onTouchMove={cancelLongPress}
      >y</span>Queens{kind === 'user' && <span className="list-tag"> · Community</span>}</h1>
      <p className="subtitle">
        Place one queen in each row, column, and color region. No two queens may touch diagonally.
      </p>
      <nav className="list-nav">
        {nextPuzzle && (
          <a className="nav-chip nav-chip-primary" href={`/puzzle/${nextPuzzle.id}`} onClick={navClick(() => onSelect(nextPuzzle.id))}>
            <span className="nav-chip-icon" aria-hidden="true">▶</span>Play Next Puzzle
          </a>
        )}
        {onOpenTips && (
          <a className="nav-chip" href="/tips-and-tricks" onClick={navClick(onOpenTips)}>
            <span className="nav-chip-icon" aria-hidden="true">💡</span>Tips &amp; Tricks
          </a>
        )}
        {onOpenDrill && (
          <a className="nav-chip" href="/drill" onClick={navClick(onOpenDrill)}>
            <span className="nav-chip-icon" aria-hidden="true">⚡</span>Tek Mode
          </a>
        )}
        {onOpenPlayers && (
          <a className="nav-chip" href="/players" onClick={navClick(onOpenPlayers)}>
            <span className="nav-chip-icon" aria-hidden="true">📊</span>Player Stats
          </a>
        )}
        {onCreate && (
          <a className="nav-chip" href="/editor" onClick={navClick(onCreate)}>
            <span className="nav-chip-icon" aria-hidden="true">✏️</span>Create a puzzle
          </a>
        )}
        {/* The other category last: it leaves this list rather than acting on it. */}
        {onOpenOfficial && (
          <a className="nav-chip" href="/" onClick={navClick(onOpenOfficial)}>
            <span className="nav-chip-icon" aria-hidden="true">♛</span>Official puzzles
          </a>
        )}
        {onOpenCommunity && (
          <a className="nav-chip" href="/community" onClick={navClick(onOpenCommunity)}>
            <span className="nav-chip-icon" aria-hidden="true">👥</span>Community puzzles
          </a>
        )}
      </nav>
      {puzzles.length === 0 && (
        <p className="empty-list">
          {kind === 'user' ? 'No community puzzles yet. ' : 'No puzzles yet. '}
          {onCreate && <a className="link-btn" href="/editor" onClick={navClick(onCreate)}>Create one</a>}
        </p>
      )}
      <div className={`puzzle-grid${kind === 'user' ? ' puzzle-grid-named' : ''}`}>
        {puzzles.map(p => {
          const completed = String(p.id) in completions;
          const skipped = !completed && skips.has(p.id);
          return (
            <a
              key={p.id}
              className={`puzzle-card${completed ? ' puzzle-card-done' : skipped ? ' puzzle-card-skipped' : ''}`}
              href={`/puzzle/${p.id}`}
              onClick={e => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); onSelect(p.id); }}
            >
              {completed && <span className="puzzle-star">★</span>}
              {skipped && <span className="puzzle-skip">○</span>}
              <span className="puzzle-name">{p.name}</span>
              {p.tags?.map(tag => (
                <span key={tag} className={`puzzle-tag puzzle-tag-${tag}`}>{tag}</span>
              ))}
              {p.solutionCount != null && p.solutionCount > 1 && (
                <span className="puzzle-multi" title={`${p.solutionCount >= 50 ? '50+' : p.solutionCount} solutions`}>
                  {p.solutionCount >= 50 ? '50+' : p.solutionCount}
                </span>
              )}
            </a>
          );
        })}
      </div>

      {sessionModal && (
        <div className="session-modal-overlay" onClick={() => setSessionModal(false)}>
          <div className="session-modal" onClick={e => e.stopPropagation()}>
            <h3>Switch Session</h3>
            {sessionsError && <p className="error">Failed to load sessions.</p>}
            {!sessions && !sessionsError && <p className="loading">Loading…</p>}
            {sessions && (
              <ul className="session-list">
                {sessions.map(s => (
                  <li
                    key={s.playerToken}
                    className={`session-item${s.playerToken === currentToken ? ' current' : ''}`}
                    onClick={() => handleSessionSwitch(s.playerToken, s.commonName)}
                  >
                    <span className="session-name">{s.commonName || <em>unnamed</em>}</span>
                    <span className="session-count">{s.puzzleCount} puzzle{s.puzzleCount !== 1 ? 's' : ''}</span>
                    <span className="session-token">{s.playerToken.slice(0, 8)}</span>
                  </li>
                ))}
              </ul>
            )}
            <button className="btn-secondary" style={{ marginTop: '1rem' }} onClick={() => setSessionModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
